import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { getIsCurrentUserBixbyComplianceAdminState } from '$/store/profile/selectors';
import { getBixbyComplianceFeatureFlagState } from '$/store/release-feature-flags/selectors';
import { AppState } from '$/store';
import { toastError } from '$/store/toast/actions';
import { ComplianceService } from '$/services';
import * as Bixby from '$/core';
import Loading from '$/components/loading/Loading';
import CompanyDeclarationFormBuilder, {
  CompanyDeclarationFormBuilderValues,
} from '$/pages/admin/client/forms/CompanyDeclarationFormBuilder';
import './ClientCompanyDeclarationForm.scss';

interface IClientCompanyDeclarationFormProps {
  isBixbyComplianceAdmin: boolean;
  isBixbyComplianceEnabled: boolean;
  client?: Bixby.Models.Client;
  dispatchToastError: (message: string) => Promise<void>;
}

const ClientCompanyDeclarationForm: React.FC<IClientCompanyDeclarationFormProps> = props => {
  const { isBixbyComplianceAdmin, isBixbyComplianceEnabled, client, dispatchToastError } = props;

  const [isLoading, setIsLoading] = useState(true as boolean);
  const [companyDeclarationForm, setCompanyDeclarationForm] = useState(
    undefined as Bixby.Models.AdminCompanyDeclarationForm | undefined
  );

  const handleFetchCompanyDeclarationForm = useCallback(async () => {
    if (client) {
      try {
        setIsLoading(true);
        const companyDeclarationForm: Bixby.Models.AdminCompanyDeclarationForm =
          await ComplianceService.getCompanyDeclarationFormForAdmin(client.id);
        setCompanyDeclarationForm(companyDeclarationForm);
      } catch (err) {
        dispatchToastError('Unable to load company declaration form');
      } finally {
        setIsLoading(false);
      }
    } else {
      dispatchToastError('Unable to retrieve company declaration form');
    }
  }, [client, dispatchToastError]);

  useEffect(() => {
    const getClientCompanyDeclarationForm = async () => {
      if (isBixbyComplianceAdmin && isBixbyComplianceEnabled && client && client.hasBixbyCompliance) {
        handleFetchCompanyDeclarationForm();
      }
    };
    getClientCompanyDeclarationForm();
  }, [isBixbyComplianceAdmin, isBixbyComplianceEnabled, client, handleFetchCompanyDeclarationForm]);

  const handleCreateCompanyDeclarationFormBuilder = useCallback(
    async (values: CompanyDeclarationFormBuilderValues) => {
      if (client) {
        try {
          setIsLoading(true);
          const companyDeclarationForm: Bixby.Models.CreateCompanyDeclarationFormRequest = {
            client_Id: client.id,
            introduction: values.introduction,
            questions: values.questions.map(
              q =>
                ({
                  id: q.id,
                  title: q.title,
                  description: q.description,
                  order: q.order,
                  questionType: q.questionType,
                  responseOptions: q.responseOptions.map(
                    r =>
                      ({
                        id: r.id,
                        order: r.order,
                        value: r.value,
                      } as Bixby.Models.CreateCompanyDeclarationFormQuestionResponseOption)
                  ),
                  validationRule: {
                    isRequired: q.validationRule.isRequired,
                    requireSpecificResponse: q.validationRule.requireSpecificResponse,
                    requireSpecificResponseValue_Id: q.validationRule.requireSpecificResponseValue_Id,
                  } as Bixby.Models.CreateCompanyDeclarationFormQuestionValidationRule,
                  conditionalLogic: q.conditionalLogic
                    ? {
                        dependsOnQuestion_Id: q.conditionalLogic.dependsOnQuestion_Id,
                        dependsOnQuestionResponseValue_Id: q.conditionalLogic.dependsOnQuestionResponseValue_Id,
                        action: q.conditionalLogic.action,
                      }
                    : (null as Bixby.Models.CreateCompanyDeclarationFormQuestionConditionalLogic | null),
                } as Bixby.Models.CreateCompanyDeclarationFormQuestion)
            ),
          };
          const newCompanyDeclarationForm: Bixby.Models.AdminCompanyDeclarationForm =
            await ComplianceService.createCompanyDeclarationForm(companyDeclarationForm);

          setCompanyDeclarationForm(newCompanyDeclarationForm);
        } catch (err) {
          dispatchToastError('Unable to save company declaration form');
        } finally {
          setIsLoading(false);
        }
      }
    },
    [client, dispatchToastError]
  );

  if (!client || !isBixbyComplianceAdmin || !isBixbyComplianceEnabled || !client.hasBixbyCompliance) {
    return null;
  } else if (isLoading) {
    return <Loading pageLevel />;
  } else if (!companyDeclarationForm) {
    return null;
  } else {
    const initialValues: CompanyDeclarationFormBuilderValues = {
      introduction: companyDeclarationForm.introduction,
      questions: companyDeclarationForm.questions,
    };

    return (
      <section className="client_company_declaration_form">
        <CompanyDeclarationFormBuilder
          initialValues={initialValues}
          isDefault={companyDeclarationForm.isDefault}
          onSubmit={handleCreateCompanyDeclarationFormBuilder}
        />
      </section>
    );
  }
};

const mapStateToProps = (state: AppState) => {
  const { isBixbyComplianceAdmin } = getIsCurrentUserBixbyComplianceAdminState(state);
  const { isBixbyComplianceEnabled } = getBixbyComplianceFeatureFlagState(state);
  return {
    isBixbyComplianceAdmin,
    isBixbyComplianceEnabled,
    client: state.clientState.currentClient,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ClientCompanyDeclarationForm);
